import { useEffect, useRef, useState } from "react";
import { ROLL_MS, prefersReducedMotion } from "./motion";

const NONE = "None";
const TYPE_MS = 45;

// The number beside the Slow Control slider. A Slow ⇄ Fast flip counts it to
// the new speed while the slider glides there (`gliding`); "None" types itself
// in at 1x and backspaces out before a number. Dragging stays instant.
export default function useSpeedLabel(fast, speed) {
  const counting = useFlipCount(fast, speed);
  const label = useTypedNone(
    counting ?? (speed === 1 ? NONE : String(speed)),
  );
  return { label, gliding: counting != null };
}

// Mid-flip, the number to show (eased from the old speed to the new), or null.
function useFlipCount(fast, speed) {
  const [prevFast, setPrevFast] = useState(fast);
  const [flip, setFlip] = useState(null);
  const lastSpeed = useRef(speed);

  if (fast !== prevFast) {
    setPrevFast(fast);
    if (!prefersReducedMotion()) {
      setFlip({ from: lastSpeed.current, to: speed, shown: lastSpeed.current });
    }
  }

  useEffect(() => {
    lastSpeed.current = speed;
  });

  const flipping = !!flip;
  useEffect(() => {
    if (!flipping) return undefined;
    const start = performance.now();
    let frame;
    const tick = (now) => {
      const t = Math.min(1, Math.max(0, (now - start) / ROLL_MS));
      if (t === 1) return setFlip(null);
      const eased = 1 - Math.pow(1 - t, 3);
      setFlip((current) =>
        current && {
          ...current,
          shown: current.from + (current.to - current.from) * eased,
        },
      );
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [flipping]);

  return flip && String(Math.round(flip.shown * 100) / 100);
}

// Types "None" in (N, No, Non, None) and backspaces it out (Non, No, N) before
// a number; number-to-number changes show at once.
function useTypedNone(label) {
  const [shown, setShown] = useState(label);
  const shownRef = useRef(label);

  useEffect(() => {
    const from = shownRef.current;
    const show = (text) => {
      shownRef.current = text;
      setShown(text);
    };
    if (from === label) return undefined;
    if ((label !== NONE && from !== NONE) || prefersReducedMotion()) {
      show(label);
      return undefined;
    }

    const frames =
      label === NONE
        ? [1, 2, 3, 4].map((n) => NONE.slice(0, n))
        : [3, 2, 1].map((n) => NONE.slice(0, n)).concat(label);
    let step = 0;
    show(frames[step]);
    const timer = setInterval(() => {
      step += 1;
      show(frames[step]);
      if (step === frames.length - 1) clearInterval(timer);
    }, TYPE_MS);
    return () => clearInterval(timer);
  }, [label]);

  return shown;
}
